import React, { useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Typography,
  Alert,
  Spinner,
} from "@material-tailwind/react";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";

const functions = getFunctions();

if (window.location.href.indexOf('localhost') > -1) {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
}

export default function UserEmailEditor() {
  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleUpdateEmail = async () => {
    if (!currentEmail || !newEmail) {
      setError("Please enter both current and new email addresses");
      return;
    }

    if (currentEmail === newEmail) {
      setError("New email must be different from current email");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      const updateUserEmail = httpsCallable(functions, "updateUserEmail");
      const result = await updateUserEmail({ currentEmail, newEmail });

      if (result.data.success) {
        setSuccess(`Email updated successfully from ${currentEmail} to ${newEmail}`);
        setCurrentEmail("");
        setNewEmail("");
      } else {
        setError(result.data.error || "Failed to update email");
      }
    } catch (err) {
      console.error("Error updating email:", err);
      setError(err.message || "Failed to update email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-12">
      <div className="mt-12 mb-8 flex flex-col gap-12">
        <Card>
          <CardHeader className="mb-8 p-6">
            <Typography variant="h6" color="blue-gray">
              Change User Email
            </Typography>
            <Typography variant="small" color="gray" className="mt-1">
              Update a user's email address in Firebase Auth and Firestore
            </Typography>
          </CardHeader>
          <CardBody>
            {error && (
              <Alert color="red" className="mb-4" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert color="green" className="mb-4" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <div className="flex flex-col gap-4 max-w-md">
              <Input
                label="Current Email"
                type="email"
                value={currentEmail}
                onChange={(e) => setCurrentEmail(e.target.value)}
                disabled={isLoading}
              />
              <Input
                label="New Email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={isLoading}
              />
              <Button
                onClick={handleUpdateEmail}
                disabled={isLoading || !currentEmail || !newEmail}
                className="flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Spinner className="h-4 w-4" />
                    Updating...
                  </>
                ) : (
                  "Update Email"
                )}
              </Button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <Typography variant="small" color="gray">
                <strong>Note:</strong> This will update the email in both Firebase Authentication
                and any Firestore documents that reference this user's email.
              </Typography>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
